import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, FileArrowDown, FilmSlate } from "@phosphor-icons/react";
import { useLang } from "../context/LanguageContext";
import api, { fileUrl } from "../lib/api";
import { youtubeEmbed } from "../lib/youtube";
import TranscriptPanel from "../components/TranscriptPanel";
import VideoWatermark from "../components/VideoWatermark";

function vimeoEmbed(url) {
  if (!url) return null;
  const m = url.match(/(?:vimeo\.com\/)(?:.*\/)?(\d+)/);
  const id = m ? m[1] : null;
  if (!id) return url.includes("player.vimeo.com") ? url : null;
  return `https://player.vimeo.com/video/${id}`;
}

export default function ContentDetail() {
  const { id } = useParams();
  const { t, lang, translateBatch } = useLang();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const ytPlayerRef = useRef(null);
  const mp4Ref = useRef(null);
  const ytIframeRef = useRef(null);
  const vimeoPlayerRef = useRef(null);
  const vimeoIframeRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api.get(`/content/${id}`).then(async (res) => {
      let d = res.data;
      if (lang === "it") {
        const [tt] = await translateBatch([d.title]);
        const [dd] = await translateBatch([d.description || ""]);
        d = { ...d, title: tt, description: dd };
      }
      if (alive) setItem(d);
    }).catch((err) => {
      if (!alive) return;
      const code = err.response?.status;
      if (code === 401 || code === 403) {
        navigate("/login", { replace: true, state: { from: `/content/${id}` } });
      } else {
        setNotFound(true);
      }
    });
    return () => { alive = false; };
  }, [id, lang, translateBatch, navigate]);

  // Track viewing time via a rolling heartbeat (only for authenticated content: non-sponsor).
  useEffect(() => {
    if (!item || item.category === "sponsor") return;
    let viewId = null;
    let seconds = 0;
    let interval;
    let cancelled = false;
    api.post(`/content/${id}/view-start`).then((res) => {
      if (cancelled) return;
      viewId = res.data.view_id;
      interval = setInterval(() => {
        if (document.visibilityState !== "visible") return;
        seconds += 15;
        api.post(`/content/view/${viewId}/heartbeat`, { seconds }).catch(() => {});
      }, 15000);
    }).catch(() => {});
    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
      if (viewId && seconds > 0) api.post(`/content/view/${viewId}/heartbeat`, { seconds }).catch(() => {});
    };
  }, [item, id]);

  // YouTube IFrame Player API — enables transcript sync (getCurrentTime + seekTo)
  useEffect(() => {
    if (!item || !item.youtube_url || !ytIframeRef.current) return;
    // Assign a unique id to the iframe so YT.Player can attach to it by id
    if (!ytIframeRef.current.id) ytIframeRef.current.id = `yt-player-${item.id}`;
    const iframeId = ytIframeRef.current.id;
    const iframe = ytIframeRef.current;

    // Low-level postMessage listener — receives info.currentTime from YouTube iframe
    const startListening = () => {
      try { iframe.contentWindow?.postMessage(JSON.stringify({ event: "listening", id: iframeId }), "*"); } catch {}
    };
    // Ping YouTube every 1s until it starts responding
    const listenTimer = setInterval(startListening, 1000);

    const onMessage = (e) => {
      if (typeof e.origin !== "string" || !e.origin.includes("youtube.com")) return;
      try {
        const data = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
        if (data && data.info && typeof data.info.currentTime === "number") {
          setCurrentTime(data.info.currentTime);
        }
      } catch {}
    };
    window.addEventListener("message", onMessage);

    const ensureApi = () => new Promise((resolve) => {
      if (window.YT && window.YT.Player) { resolve(); return; }
      const existing = document.getElementById("yt-iframe-api");
      if (!existing) {
        const tag = document.createElement("script");
        tag.id = "yt-iframe-api";
        tag.src = "https://www.youtube.com/iframe_api";
        document.body.appendChild(tag);
      }
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { if (prev) prev(); resolve(); };
      const iv = setInterval(() => { if (window.YT && window.YT.Player) { clearInterval(iv); resolve(); } }, 150);
      setTimeout(() => clearInterval(iv), 8000);
    });

    let poll = null;
    let destroyed = false;
    ensureApi().then(() => {
      if (destroyed) return;
      const el = document.getElementById(iframeId);
      if (!el) return;
      try {
        ytPlayerRef.current = new window.YT.Player(iframeId, {
          events: {
            onReady: () => {
              // Start polling once player is ready (works even when API doesn't fire progress events)
              poll = setInterval(() => {
                try {
                  const p = ytPlayerRef.current;
                  if (p && typeof p.getCurrentTime === "function") {
                    const t = p.getCurrentTime();
                    if (typeof t === "number" && !isNaN(t)) setCurrentTime(t);
                  }
                } catch {}
              }, 400);
            },
          },
        });
      } catch (e) { /* ignore */ }
    });
    return () => {
      destroyed = true;
      clearInterval(listenTimer);
      if (poll) clearInterval(poll);
      window.removeEventListener("message", onMessage);
      try { ytPlayerRef.current?.destroy?.(); } catch {}
      ytPlayerRef.current = null;
    };
  }, [item]);

  // Vimeo Player API — enables transcript sync
  useEffect(() => {
    if (!item || !item.vimeo_url || !vimeoIframeRef.current) return;
    let destroyed = false;
    import("@vimeo/player").then(({ default: VimeoPlayer }) => {
      if (destroyed) return;
      try {
        const p = new VimeoPlayer(vimeoIframeRef.current);
        vimeoPlayerRef.current = p;
        p.on("timeupdate", (data) => { setCurrentTime(data.seconds || 0); });
      } catch {}
    });
    return () => {
      destroyed = true;
      try { vimeoPlayerRef.current?.destroy?.(); } catch {}
      vimeoPlayerRef.current = null;
    };
  }, [item]);

  const handleTranscriptSeek = (seconds) => {
    // Optimistic: immediately move the highlight even before player ack
    setCurrentTime(seconds);
    if (ytPlayerRef.current?.seekTo) {
      try { ytPlayerRef.current.seekTo(seconds, true); ytPlayerRef.current.playVideo?.(); } catch {}
    }
    // Fallback: raw postMessage to the iframe
    try {
      ytIframeRef.current?.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func: "seekTo", args: [seconds, true] }), "*"
      );
      ytIframeRef.current?.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func: "playVideo", args: [] }), "*"
      );
    } catch {}
    if (vimeoPlayerRef.current?.setCurrentTime) {
      try { vimeoPlayerRef.current.setCurrentTime(seconds).then(() => vimeoPlayerRef.current.play?.()); return; } catch {}
    }
    if (mp4Ref.current) {
      try { mp4Ref.current.currentTime = seconds; mp4Ref.current.play(); return; } catch {}
    }
  };

  const handleSpeedChange = (rate) => {
    setPlaybackRate(rate);
    try { ytPlayerRef.current?.setPlaybackRate?.(rate); } catch {}
    try { vimeoPlayerRef.current?.setPlaybackRate?.(rate); } catch {}
    if (mp4Ref.current) { try { mp4Ref.current.playbackRate = rate; } catch {} }
  };

  if (notFound) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6">
        <div className="text-center">
          <p className="font-head text-3xl text-white">404</p>
          <Link to="/library/video" className="mt-4 inline-block font-mono text-xs uppercase tracking-widest text-cyan">{t("back_to_library")}</Link>
        </div>
      </div>
    );
  }
  if (!item) return <div className="min-h-screen px-6 pt-40 font-mono text-slate-500">Loading…</div>;

  const embed = vimeoEmbed(item.vimeo_url);
  const ytEmbed = item.youtube_url ? youtubeEmbed(item.youtube_url) : null;
  const isVideoFile = item.file_id && item.file_type === "mp4";

  return (
    <div data-testid="content-detail-page" className="min-h-screen px-6 pt-28 pb-28 md:px-10">
      <div className="mx-auto max-w-6xl">
        <Link data-testid="back-link" to={`/library/${item.category === "presentation" ? "presentation" : item.category === "other" ? "other" : "video"}`} className="mb-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-slate-400 transition-colors hover:text-cyan">
          <ArrowLeft size={16} /> {t("back_to_library")}
        </Link>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="mb-6 flex items-center gap-3">
            <span className="border border-cyan/40 bg-cyan/5 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-cyan">
              {item.category === "video" ? t("video") : item.category === "presentation" ? t("presentations") : t("other")}
            </span>
            {item.speaker && <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">{item.speaker}</span>}
            {item.recorded_at && !isNaN(new Date(item.recorded_at).getTime()) && (
              <span data-testid="detail-recorded-at" className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                · {new Date(item.recorded_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}
              </span>
            )}
          </div>
          <h1 className="font-head text-4xl font-black uppercase leading-none tracking-tight text-white md:text-6xl">{item.title}</h1>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }} className="mt-10">
          {/* YOUTUBE */}
          {ytEmbed && (
            <div className="relative aspect-video w-full overflow-hidden border border-white/10 bg-black">
              <iframe
                ref={ytIframeRef}
                data-testid="youtube-iframe"
                src={`${ytEmbed}?rel=0&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`}
                title={item.title}
                className="absolute inset-0 h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
              />
              <VideoWatermark />
            </div>
          )}
          {/* VIDEO (vimeo or mp4) */}
          {!ytEmbed && embed && (
            <div className="relative aspect-video w-full overflow-hidden border border-white/10 bg-black">
              <iframe
                ref={vimeoIframeRef}
                data-testid="vimeo-iframe"
                src={embed}
                title={item.title}
                className="absolute inset-0 h-full w-full"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
              <VideoWatermark />
            </div>
          )}
          {!ytEmbed && !embed && isVideoFile && (
            <div className="relative aspect-video w-full overflow-hidden border border-white/10 bg-black">
              <video
                ref={mp4Ref}
                data-testid="mp4-player"
                src={fileUrl(item.file_id, { signed: true })}
                controls
                onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
                className="absolute inset-0 h-full w-full"
              />
              <VideoWatermark />
            </div>
          )}
          {/* PRESENTATION / OTHER FILE (non-mp4) */}
          {!ytEmbed && !embed && !isVideoFile && item.file_id && (
            <div className="flex flex-col gap-6">
              {item.file_type === "pptx" ? (
                <div className="relative aspect-video w-full overflow-hidden border border-white/10 bg-ink-800">
                  <iframe
                    data-testid="pptx-viewer"
                    title={item.title}
                    src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl(item.file_id))}`}
                    className="absolute inset-0 h-full w-full"
                    frameBorder="0"
                  />
                </div>
              ) : (
                <div className="flex items-center gap-4 border border-white/10 bg-ink-700/40 p-8">
                  <FileArrowDown size={48} weight="thin" className="text-cyan" />
                  <div>
                    <p className="font-head text-xl text-white">{item.file_name || "File"}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{item.file_type}</p>
                  </div>
                </div>
              )}
              <a
                data-testid="download-file-btn"
                href={fileUrl(item.file_id)}
                target="_blank"
                rel="noreferrer"
                className="group inline-flex w-fit items-center gap-3 border border-white/20 px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-white transition-colors hover:border-cyan hover:text-cyan"
              >
                <FileArrowDown size={16} weight="bold" /> {t("download")}
              </a>
            </div>
          )}
          {/* Nothing linked yet */}
          {!ytEmbed && !embed && !item.file_id && (
            <div className="flex aspect-video w-full items-center justify-center border border-dashed border-white/15 bg-ink-800/50">
              <div className="text-center">
                <FilmSlate size={48} weight="thin" className="mx-auto text-slate-600" />
                <p className="mt-3 font-mono text-xs uppercase tracking-widest text-slate-500">Coming soon</p>
              </div>
            </div>
          )}
        </motion.div>

        {/* Playback speed control (works for YouTube, Vimeo, MP4) */}
        {(ytEmbed || embed || isVideoFile) && (
          <div data-testid="speed-control" className="mt-4 flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Speed:</span>
            {[0.5, 0.75, 1, 1.25, 1.5, 2].map((r) => {
              const on = playbackRate === r;
              return (
                <button
                  key={r}
                  data-testid={`speed-${r}`}
                  onClick={() => handleSpeedChange(r)}
                  className="border px-2 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors"
                  style={{
                    borderColor: on ? "#00F0FF" : "rgba(255,255,255,0.15)",
                    backgroundColor: on ? "rgba(0,240,255,0.12)" : "transparent",
                    color: on ? "#00F0FF" : "#94A3B8",
                  }}
                >
                  {r}x
                </button>
              );
            })}
          </div>
        )}

        {/* Transcript panel temporarily hidden until Whisper auto-transcription
            is enabled — YouTube auto-caption fetching is blocked from the
            hosting IP by YouTube itself. Re-enable this block once transcripts
            are populated (manual upload or Whisper) to avoid showing an empty
            panel to members. */}
        {false && (ytEmbed || embed || isVideoFile) && (
          <TranscriptPanel
            contentId={id}
            currentTime={currentTime}
            onSeek={handleTranscriptSeek}
          />
        )}

        {item.description && (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }} className="mt-10 max-w-3xl font-body text-lg leading-relaxed text-slate-300">
            {item.description}
          </motion.p>
        )}
      </div>
    </div>
  );
}
